import { useQuery, useQueryClient } from '@tanstack/react-query'
import { TaskService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'

export const tasksQueryKeys = {
  all: () => ['tasks'] as const,
  list: () => ['tasks', 'list'] as const,
  detail: (taskId: string) => ['tasks', 'detail', taskId] as const,
}

const fetchTasks = async (): Promise<TaskService.Task[]> => {
  const response = await apis.taskService.rootGet()
  return response.data
}

export const useTasks = () => {
  const query = useQuery({
    queryKey: tasksQueryKeys.list(),
    queryFn: fetchTasks,
    staleTime: 30 * 60 * 1000, // 30 minutes
  })

  return {
    tasks: query.data ?? [],
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  } as const
}

export const useTask = (taskId: string | undefined) => {
  const queryClient = useQueryClient()
  
  const query = useQuery({
    queryKey: tasksQueryKeys.detail(taskId ?? ''),
    queryFn: async () => {
      if (!taskId) {
        throw new Error('Task ID is required')
      }
      
      // First, try to get the task from the list data
      const listData = queryClient.getQueryData<TaskService.Task[]>(
        tasksQueryKeys.list()
      )
      
      if (listData) {
        const taskFromList = listData.find(t => t.id === taskId)
        if (taskFromList) {
          return taskFromList
        }
      }
      
      // If not found in list, we would fetch from API here
      // For now, throw an error since there's no detail endpoint visible
      throw new Error('Task not found')
    },
    enabled: !!taskId,
    staleTime: 30 * 60 * 1000, // 30 minutes
  })

  // Custom refetch that invalidates the list cache to ensure fresh data
  const refetch = async () => {
    // Invalidate the list cache so we get fresh data
    await queryClient.invalidateQueries({ queryKey: tasksQueryKeys.list() })
    // Then refetch the detail query
    return query.refetch()
  }

  return {
    task: query.data,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch,
  } as const
}
